import { Divider, Stack, Typography, useTheme } from "@mui/material";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import toast from "react-hot-toast";
import {
  useGetAllBoards,
  useGetIntegrationNotificationSettingsById,
  useGetLevelAuthConfig,
  useUpdateLevelAuthConfig,
  useUpsertIntegrationNotificationSettings,
} from "controller";
import levelRightBg from "assets/images/integration/level/levelRightBg.webp";
import { CheckCircleIcon, CircularLoader, SelectField } from "components";
import { ToggleButton } from "components/toggle";
import { INTEGRATION_SOURCE_IDS } from "../../integrationList";

export const ChooseLevelDefaultBoard = forwardRef(
  ({ onStepChange }: { onStepChange: (step: number) => void }, ref) => {
    const theme = useTheme();
    const { data: levelAuthConfig } = useGetLevelAuthConfig();
    const { mutateAsync: updateLevelAuthConfig } = useUpdateLevelAuthConfig();
    const { data: boards } = useGetAllBoards({ serviceType: "ticket_board" });
    const { data: levelNotificationSettings } =
      useGetIntegrationNotificationSettingsById(INTEGRATION_SOURCE_IDS.LEVEL);
    const {
      mutateAsync: upsertIntegrationNotificationSettings,
      isLoading: isUpsertIntegrationNotificationSettingsLoading,
    } = useUpsertIntegrationNotificationSettings();

    const [isLevelNotificationsEnabled, setIsLevelNotificationsEnabled] =
      useState(levelNotificationSettings?.is_enabled ?? true);
    useEffect(() => {
      setIsLevelNotificationsEnabled(
        levelNotificationSettings?.is_enabled ?? true,
      );
    }, [levelNotificationSettings]);

    const onToggle = async () => {
      const initialSetting = levelNotificationSettings?.is_enabled ?? true;
      try {
        setIsLevelNotificationsEnabled((prev: boolean) => !prev);
        await upsertIntegrationNotificationSettings({
          source_id: INTEGRATION_SOURCE_IDS.LEVEL,
          is_enabled: !initialSetting,
        });
      } catch (err: any) {
        setIsLevelNotificationsEnabled(initialSetting);
        toast.error("Failed to update notification settings");
      }
    };

    const TICKET_HOME_BOARD_ID = "a6e692ea-39c9-418e-8e9b-649e262a5100";
    const [board, setBoard] = useState(
      levelAuthConfig?.board || TICKET_HOME_BOARD_ID,
    );

    useEffect(() => {
      if (levelAuthConfig?.data?.board) {
        setBoard(levelAuthConfig?.data?.board);
      }
    }, [levelAuthConfig]);

    const onSubmit = async () => {
      try {
        await updateLevelAuthConfig({
          api_key: levelAuthConfig?.api_key,
          webhook_url: levelAuthConfig?.webhook_url,
          webhook_secret: levelAuthConfig?.webhook_secret,
          board: board,
        });
        toast.success("Default board set successfully");
        return true;
      } catch (error: any) {
        toast.error("Failed to set default board");
        return false;
      }
    };

    useImperativeHandle(ref, () => ({
      onSubmit,
    }));

    return (
      <Stack direction="row" height="100%">
        <Stack flex={1} alignItems={"center"} justifyContent={"center"}>
          <Stack gap="24px" width="63%">
            <Stack direction="row" alignItems="center" gap="8px">
              <CheckCircleIcon
                color="success"
                width="24px"
                height="24px"
                sx={{
                  marginTop: "4px",
                }}
              />
              <Typography variant="h3">Awesome, you’re connected!</Typography>
            </Stack>
            <Stack gap="16px">
              <Typography variant="body-medium">
                Choose your default board
              </Typography>
              <SelectField
                name="board"
                options={boards?.result}
                value={board}
                onValueChange={(value) => setBoard(value)}
                valueMappingKey="ticket_board_id"
                labelMappingKey="ticket_board_name"
                isSearchable
                searchFieldProps={{
                  placeholder: "Search board",
                }}
              />
            </Stack>
            <Divider />
            <Stack gap="16px">
              <Typography variant="body-medium">Alert notifications</Typography>
              <Stack
                p="16px"
                direction="row"
                alignItems="center"
                gap="8px"
                border={`1px solid ${theme.palette.neutral["015"]}`}
                borderRadius="4px"
              >
                <ToggleButton
                  isChecked={isLevelNotificationsEnabled}
                  onChange={onToggle}
                  isDisabled={isUpsertIntegrationNotificationSettingsLoading}
                />
                <Typography variant="button-label">
                  Enable to get notifications for alerts
                </Typography>
                {isUpsertIntegrationNotificationSettingsLoading && (
                  <CircularLoader
                    size="16px"
                    sx={{
                      ml: "auto",
                    }}
                  />
                )}
              </Stack>
            </Stack>
          </Stack>
        </Stack>
        <Stack flex={1}>
          <img
            width="100%"
            height="100%"
            src={levelRightBg}
            alt="level background"
            style={{
              objectFit: "cover",
            }}
          />
        </Stack>
      </Stack>
    );
  },
);
